// Agrégation temporelle : regroupe des éléments datés en buckets jour/semaine/mois puis réduit chacun.

import { formatISO } from 'date-fns';
import { bucketKey, bucketStart, toDate } from './datetime';
import type { Granularity, TimeBucket } from './types';

// Regroupe par bucket, réduit chaque groupe, renvoie trié par ordre chronologique.
export function aggregateBy<T, R>(
  items: readonly T[],
  getDate: (item: T) => string,
  granularity: Granularity,
  reduce: (group: T[]) => R,
): TimeBucket<R>[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = bucketKey(getDate(item), granularity);
    const arr = groups.get(key);
    if (arr) arr.push(item);
    else groups.set(key, [item]);
  }

  return [...groups.entries()]
    .map(([key, group]) => {
      const start = bucketStart(getDate(group[0]!), granularity);
      return {
        key,
        start: formatISO(start, { representation: 'date' }),
        value: reduce(group),
      };
    })
    .sort((a, b) => a.start.localeCompare(b.start));
}

// Comble les jours manquants (valeur `empty`) pour un axe continu. Granularité `day` uniquement.
export function fillDailyGaps<R>(buckets: readonly TimeBucket<R>[], empty: R): TimeBucket<R>[] {
  if (buckets.length === 0) return [];
  const out: TimeBucket<R>[] = [];
  const first = toDate(buckets[0]!.start);
  const last = toDate(buckets[buckets.length - 1]!.start);
  const byKey = new Map(buckets.map((b) => [b.start, b]));

  for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
    const key = formatISO(d, { representation: 'date' });
    const existing = byKey.get(key);
    out.push(existing ?? { key, start: key, value: empty });
  }
  return out;
}
